/**
 * Diagnostic media library
 *
 * Maps a scenario / modifier key → an array of media items the instructor can
 * pop up on the learner's monitor (X-rays, ECGs, POCUS clips, clinical photos).
 *
 * Each item:
 *   id    — stable unique id (used for toggle comparison + React keys)
 *   type  — 'image' | 'video'
 *   src   — path under /public (e.g. '/media/simtech/xray/Pneumothorax.jpg')
 *   label — short button caption shown to the instructor + as overlay caption
 *
 * Media assets are the A/V stimuli library from The SimTech (thesimtech.org),
 * used here for non-commercial simulation education (their permissions allow
 * non-commercial use by individuals / higher-education institutions without
 * attribution). Source credit is shown on the overlay as good practice.
 *
 * To add media: drop the file in /public/media/ and add an entry here keyed by
 * the scenario or modifier key (the same keys used in ScenarioSelector).
 */

// Shown on the media overlay as a source credit.
export const MEDIA_ATTRIBUTION = 'Media: thesimtech.org A/V stimuli';

const ECG = '/media/simtech/ecg/';
const XR = '/media/simtech/xray/';
const US = '/media/simtech/ultrasound/';
const CLIN = '/media/simtech/clinical/';

const ecg = (id, file, label = '🫀 ECG') => ({ id, type: 'image', src: ECG + file, label });
const cxr = (id, file, label = '🩻 CXR') => ({ id, type: 'image', src: XR + file, label });
const pocus = (id, file, label = '🎥 POCUS') => ({ id, type: 'video', src: US + file, label });
const photo = (id, file, label = '📷 Photo') => ({ id, type: 'image', src: CLIN + file, label });

export const SCENARIO_MEDIA = {
  // ── Rhythms (ECGs) ─────────────────────────────────────────────────────────
  stElevation:          [ecg('stemi-ant', 'STEMIAnterior.jpg', '🫀 STEMI (Ant)'), ecg('stemi-post', 'STEMIPosterior.jpg', '🫀 STEMI (Post)')],
  rcaMI:                [ecg('stemi-inf', 'STEMIInferior.jpg', '🫀 Inferior STEMI')],
  epicMyocardialIschemia: [ecg('acute-mi', 'AcuteMI.jpg'), ecg('wellens', 'WellensSyndromeTypeA.jpg', '🫀 Wellens')],
  rapidAfibHypotension: [ecg('afib', 'AtrialFibrillation.jpg', '🫀 AFib')],
  rapidSVTHypotension:  [ecg('svt', 'SupraventricularTachycardiaSVT.jpg', '🫀 SVT')],
  epicSVT:              [ecg('svt', 'SupraventricularTachycardiaSVT.jpg', '🫀 SVT')],
  palsSvtStable:        [ecg('svt', 'SupraventricularTachycardiaSVT.jpg', '🫀 SVT')],
  palsSvtUnstable:      [ecg('svt', 'SupraventricularTachycardiaSVT.jpg', '🫀 SVT')],
  vtach:                [ecg('vt', 'VentricularTachycardiaMonomorphic.jpg', '🫀 VT')],
  last:                 [ecg('vt', 'VentricularTachycardiaMonomorphic.jpg', '🫀 VT')],
  tdp:                  [ecg('tdp', 'TorsadesdePointes.jpg', '🫀 TdP')],
  vf:                   [ecg('vf', 'VentricularFibrillation.jpg', '🫀 VF')],
  obgynCardiacArrest:   [ecg('vf', 'VentricularFibrillation.jpg', '🫀 VF')],
  palsVfArrest:         [ecg('vf', 'VentricularFibrillation.jpg', '🫀 VF')],
  epicVFVT:             [ecg('vf', 'VentricularFibrillation.jpg', '🫀 VF/VT')],
  tachycardia:          [ecg('sinus-tachy', 'SinusTachycardia.jpg', '🫀 Sinus Tachy')],
  bradycardia:          [ecg('sinus-brady', 'SinusBradycardia.jpg', '🫀 Sinus Brady')],
  epicBradycardia:      [ecg('sinus-brady', 'SinusBradycardia.jpg', '🫀 Brady')],
  palsBradycardiaArrest:[ecg('sinus-brady', 'SinusBradycardia.jpg', '🫀 Brady')],
  hyperkalemia:         [ecg('hyperk', 'Hyperkalemia.jpg', '🫀 HyperK ECG')],
  hyperkalemicArrest:   [ecg('hyperk', 'Hyperkalemia.jpg', '🫀 HyperK ECG')],

  // ── X-rays ─────────────────────────────────────────────────────────────────
  tensionPneumothorax:  [cxr('ptx', 'Pneumothoraxsevere1.jpg'), cxr('ptx-lat', 'PneumothoraxLateralsevere.jpg', '🩻 CXR (Lat)')],
  epicPneumothorax:     [cxr('ptx', 'Pneumothorax.jpg')],
  palsTensionPneumo:    [cxr('ptx', 'Pneumothorax.jpg')],
  cardiacTamponade:     [cxr('tamponade-cxr', 'PericardialTamponade.jpg'), pocus('tamponade-us', 'ultrasound_-_cardiac_-_tamponade-720p.mp4', '🎥 POCUS Tamponade')],
  obgynPeripartumCM:    [cxr('ppcm-cxr', 'Cardiomegalysevere.jpg', '🩻 Cardiomegaly'), cxr('ppcm-edema', 'FluidLevelsBilateralmoderate.jpg', '🩻 Pulm Oedema')],
  obPulmonaryEdema:     [cxr('edema', 'FluidLevelsBilateralmoderate.jpg', '🩻 Pulm Oedema'), cxr('cardiomeg', 'Cardiomegalymoderate.jpg', '🩻 Cardiomegaly')],
  epicRightHeartFailure:[cxr('cardiomeg', 'Cardiomegalymoderate.jpg', '🩻 Cardiomegaly')],
  palsPneumonia:        [cxr('pna-seg', 'PneumoniaRightsideSegmental1.jpg', '🩻 Pneumonia'), cxr('pna-diff', 'PneumoniaBilateraldiffuse.jpg', '🩻 Pneumonia (diffuse)')],
  obAspiration:         [cxr('aspiration', 'PneumoniaRightsideSegmental1.jpg', '🩻 Infiltrate')],
  pneumoperitoneum:     [cxr('pneumoperit', 'Pneumoperitonium.jpg', '🩻 Free Air')],

  // ── POCUS (FAST) ───────────────────────────────────────────────────────────
  obgynHemorrhagicShock:[pocus('fast-pos', 'ultrasound_-_fast_-_free_fluid_ruq-360p.mp4', '🎥 FAST (free fluid)')],
  epicHemorrhage:       [pocus('fast-pos', 'ultrasound_-_fast_-_free_fluid_ruq-360p.mp4', '🎥 FAST (free fluid)')],
  epicTrauma:           [pocus('fast-pos', 'ultrasound_-_fast_-_free_fluid_ruq-360p.mp4', '🎥 FAST (free fluid)')],

  // ── Clinical photos ────────────────────────────────────────────────────────
  anaphylaxis:          [photo('anaphylaxis-rash', 'AnaphylaxisSkinRash.jpg', '📷 Rash'), photo('anaphylaxis', 'Anaphylaxis.jpg', '📷 Anaphylaxis')],
  epicAnaphylaxis:      [photo('anaphylaxis-rash', 'AnaphylaxisSkinRash.jpg', '📷 Rash')],
};

/** Return the media items registered for a scenario/modifier key (or []). */
export function getScenarioMedia(key) {
  return SCENARIO_MEDIA[key] ?? [];
}
